import { Alert } from '@linktr.ee/arbor/components/Alert';
import { CircleNotchIcon, InfoIcon } from '@linktr.ee/arbor/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import Layout from '@shared/components/Layout';
import bannerLoginImage from '@universal-login/images/banner-login-desktop.webp';
import bannerSignupImage from '@universal-login/images/banner-signup-desktop.webp';

interface ArkoseGateScreenProps {
  authFlow: 'login' | 'signup';
  backHref?: string;
  onContinue: () => Promise<void>;
  errorText?: string;
}

const ArkoseGateScreen = ({
  authFlow,
  backHref,
  onContinue,
  errorText,
}: ArkoseGateScreenProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasSubmittedRef = useRef(false);

  const continueToNextStep = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(undefined);
    try {
      await onContinue();
    } catch {
      setSubmitError(t('Browser check failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [onContinue, t]);

  useEffect(() => {
    if (hasSubmittedRef.current || errorText) {
      return;
    }

    hasSubmittedRef.current = true;
    void continueToNextStep();
  }, [continueToNextStep, errorText]);

  const isSignup = authFlow === 'signup';
  const error =
    submitError ||
    (errorText ? t('Browser check failed. Please try again.') : undefined);

  return (
    <Layout
      heading={t('Just a moment')}
      subHeading={t("We're making sure it's really you")}
      variant="banner"
      bannerImageDesktop={isSignup ? bannerSignupImage : bannerLoginImage}
    >
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        {(!error || isSubmitting) && (
          <div
            aria-live="polite"
            role="status"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-black"
          >
            <span className="sr-only">{t('Loading')}</span>
            <span className="animate-spin">
              <CircleNotchIcon />
            </span>
          </div>
        )}
        {error && (
          <>
            <div role="alert" className="w-full">
              <Alert
                status="error"
                variant="page-level"
                icon={<InfoIcon />}
                title={t('Something went wrong')}
                message={error}
                className="text-left"
              />
            </div>
            <Button
              label={t('Try again')}
              onClick={() => {
                void continueToNextStep();
              }}
              state={isSubmitting ? 'loading' : 'default'}
              variant="primary"
            />
            {backHref && (
              <AuthLink>
                <a href={backHref}>{t('Back')}</a>
              </AuthLink>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ArkoseGateScreen;
